import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetStockAdjustmentQuery,
  useApproveStockAdjustmentMutation,
  useRejectStockAdjustmentMutation,
  useDeleteStockAdjustmentMutation,
} from "../../stockApi";
import { useCanApproveAdjustment, useCanDeleteAdjustment } from "../../useStockPermissions";
import StatusBadge from "../../components/StatusBadge";
import ReasonPrompt from "../../components/ReasonPrompt";
import { paths } from "../../../../routes/paths";

const StockAdjustmentDetail = () => {
  const { recordGuid } = useParams();
  const canApprove = useCanApproveAdjustment();
  const canDelete = useCanDeleteAdjustment();
  const { data: adj, isLoading, isError, error } = useGetStockAdjustmentQuery(recordGuid);
  const [approve] = useApproveStockAdjustmentMutation();
  const [reject] = useRejectStockAdjustmentMutation();
  const [deleteAdjustment] = useDeleteStockAdjustmentMutation();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const handleApprove = async () => {
    try {
      await approve(recordGuid).unwrap();
      toast.success("Adjustment approved - stock balance updated");
    } catch (e) {
      toast.error(e?.message || "Could not approve the adjustment");
    }
  };

  const handleReject = async (rejectionReason) => {
    if (!rejectionReason.trim()) return toast.error("A rejection reason is required");
    try {
      await reject({ recordGuid, rejectionReason }).unwrap();
      toast.success("Adjustment rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the adjustment");
    }
  };

  const handleDelete = async (deleteReason) => {
    try {
      await deleteAdjustment({ recordGuid, deleteReason: deleteReason || null }).unwrap();
      toast.success("Adjustment deleted - any approved balance change was reversed");
      setShowDeleteForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not delete the adjustment");
    }
  };

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !adj) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockAdjustments} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Adjustments</Link>
          <div className="alert alert-danger">Could not load this adjustment ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockAdjustments} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Adjustments</Link>
              <h4>{adj.adjustmentNo} <StatusBadge status={adj.status} /></h4>
              <h6>{adj.branchCode} - {adj.adjustmentType === "INCREASE" ? "Increase" : "Decrease"}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {canApprove && adj.status === "SUBMITTED" && (
              <>
                <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove}>
                  <CheckCircle size={16} /> Approve
                </button>
                <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                  <XCircle size={16} /> Reject
                </button>
              </>
            )}
            {canDelete && !adj.isDeleted && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowDeleteForm((s) => !s)}>
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}
        {showDeleteForm && (
          <ReasonPrompt
            label="Delete Reason (optional) - if this adjustment was APPROVED, its balance change is reversed"
            confirmLabel="Delete Adjustment"
            required={false}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteForm(false)}
          />
        )}

        {adj.status === "REJECTED" && adj.rejectionReason && (
          <div className="alert alert-danger">Rejected by {adj.rejectedByName}: {adj.rejectionReason}</div>
        )}
        {adj.status === "SUBMITTED" && (
          <div className="alert alert-info">Stock balance will only be updated once this adjustment is approved.</div>
        )}
        {adj.isDeleted && (
          <div className="alert alert-danger">
            Deleted by {adj.deletedByName} on {new Date(adj.deletedDate).toLocaleString()}
            {adj.deleteReason ? `: ${adj.deleteReason}` : ""}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6"><strong>Reason:</strong> {adj.reason}</div>
              <div className="col-md-6"><strong>Date:</strong> {new Date(adj.adjustmentDate).toLocaleDateString()}</div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead><tr><th>#</th><th>Product</th><th>Adjusted Qty</th><th>Unit</th><th>Reason</th></tr></thead>
                <tbody>
                  {adj.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{l.productName} ({l.productCode})</td>
                      <td>{l.adjustedQty}</td>
                      <td>{l.unit}</td>
                      <td>{l.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-muted small mt-3">
              Created by {adj.createdByName} on {new Date(adj.createdDate).toLocaleString()}
              {adj.approvedByName && ` - Approved by ${adj.approvedByName} on ${new Date(adj.approvedDate).toLocaleString()}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentDetail;
